import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { fetchHeroSlides } from '../utils/heroApi';

const HERO_CACHE_KEY = 'jaz_hero_slides_v2';
const MOBILE_HERO_MQ = '(max-width: 992px)';

function getMobileLayoutMetrics(width) {
    if (width <= 768) return { strip: 52, gap: 8, padding: 8 };
    return { strip: 72, gap: 10, padding: 12 };
}

function getMobileActiveHeight(containerHeight, slideCount, metrics) {
    const { strip, gap, padding } = metrics;
    const available = containerHeight - padding * 2;
    const collapsedTotal = (slideCount - 1) * strip + (slideCount - 1) * gap;
    return Math.max(strip, available - collapsedTotal);
}

function getMobileItemHeight(index, activeIndex, slideCount, activeHeight, stripHeight) {
    return index === activeIndex ? activeHeight : stripHeight;
}

const FALLBACK_SLIDES = [
    {
        id: 1,
        collapsedText: 'Custom Software Development',
        headline: 'Custom Software\n Solutions',
        subText: 'Software built around your workflow\n Streamline operations and scale faster.',
        contactLink: '/contact',
        imageDesktop: '/images/bg/bg-image-88.webp',
        imageMobile: '/images/bg/bg-image-88.webp',
        imageAlt: 'Custom software development',
        imageBlur: 2,
    },
    {
        id: 2,
        collapsedText: 'Mobile Apps for Android & iOS',
        headline: 'Android & iOS\n Mobile Apps',
        subText: 'High-performance apps for every device\n Modern UI/UX with seamless reliability.',
        contactLink: '/contact',
        imageDesktop: '/images/bg/bg-image-222.webp',
        imageMobile: '/images/bg/bg-image-222.webp',
        imageAlt: 'Mobile app development',
        imageBlur: 2,
    },
    {
        id: 3,
        collapsedText: 'Premium Websites & E-Commerce',
        headline: 'Premium Website\n & E-Commerce',
        subText: 'Beautiful, fast websites and online stores\n Designed to convert visitors into customers.',
        contactLink: '/contact',
        imageDesktop: '/images/bg/bg-image-333.webp',
        imageMobile: '/images/bg/bg-image-333.webp',
        imageAlt: 'Web and e-commerce development',
        imageBlur: 2,
    },
    {
        id: 4,
        collapsedText: 'Custom ERP & CRM Systems',
        headline: 'Custom ERP\n & CRM',
        subText: 'Unified systems for sales, operations & teams\n Manage customers, inventory, and growth in one place.',
        contactLink: '/contact',
        imageDesktop: '/images/services/servhero.webp',
        imageMobile: '/images/services/servhero.webp',
        imageAlt: 'Custom ERP and CRM software',
        imageBlur: 2,
    },
];

function readCachedSlides() {
    try {
        const raw = sessionStorage.getItem(HERO_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (_) {
        return null;
    }
}

function writeCachedSlides(slides) {
    try {
        sessionStorage.setItem(HERO_CACHE_KEY, JSON.stringify(slides));
    } catch (_) {}
}

function preloadSlideImage(slide) {
    return new Promise((resolve) => {
        const src = resolveImageSrc(slide.imageDesktop);
        if (!src) {
            resolve();
            return;
        }
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });
}

function MultilineText({ text, className, Tag = 'span' }) {
    if (!text) return null;
    const lines = String(text).split('\n');
    return (
        <Tag className={className}>
            {lines.map((line, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                </React.Fragment>
            ))}
        </Tag>
    );
}

function resolveImageSrc(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${process.env.PUBLIC_URL || ''}${path}`;
}

function useMobileHeroLayout() {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(MOBILE_HERO_MQ).matches : false
    );

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_HERO_MQ);
        const onChange = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', onChange);
        setIsMobile(mq.matches);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    return isMobile;
}

const HeroSection = () => {
    const cachedSlides = typeof window !== 'undefined' ? readCachedSlides() : null;
    const [slides, setSlides] = useState(cachedSlides || []);
    const [activeSlide, setActiveSlide] = useState(cachedSlides?.[0]?.id ?? null);
    const [ready, setReady] = useState(Boolean(cachedSlides?.length));
    const [layoutMeasured, setLayoutMeasured] = useState(false);
    const [progress, setProgress] = useState(0);
    const isMobileLayout = useMobileHeroLayout();
    const DURATION = 5000;
    const INTERVAL_TIME = 50;

    const heroRef = useRef(null);
    const [mobileLayout, setMobileLayout] = useState({
        activeHeight: 0,
        metrics: getMobileLayoutMetrics(992),
    });

    useEffect(() => {
        let cancelled = false;

        const applySlides = (nextSlides) => {
            writeCachedSlides(nextSlides);
            setSlides(nextSlides);
            setActiveSlide((prev) => {
                const stillExists = nextSlides.some((s) => s.id === prev);
                return stillExists ? prev : nextSlides[0].id;
            });
            setReady(true);
            preloadSlideImage(nextSlides[0]);
        };

        (async () => {
            const data = await fetchHeroSlides();
            if (cancelled) return;

            const nextSlides = data.length ? data : FALLBACK_SLIDES;
            const unchanged =
                cachedSlides &&
                JSON.stringify(cachedSlides) === JSON.stringify(nextSlides);

            if (unchanged) {
                if (!ready) setReady(true);
                return;
            }

            applySlides(nextSlides);
        })();

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const slideCount = slides.length;

    const getNextSlideIdLoop = useCallback(() => {
        const idx = slides.findIndex((s) => s.id === activeSlide);
        const next = idx >= 0 ? slides[(idx + 1) % slideCount] : slides[0];
        return next?.id ?? activeSlide;
    }, [activeSlide, slides, slideCount]);

    const handleSlideChange = (id) => {
        if (id === activeSlide) return;
        setActiveSlide(id);
        setProgress(0);
    };

    useEffect(() => {
        const el = heroRef.current;
        if (!el || !isMobileLayout) return undefined;

        const updateLayout = () => {
            const metrics = getMobileLayoutMetrics(window.innerWidth);
            setMobileLayout({
                metrics,
                activeHeight: getMobileActiveHeight(el.clientHeight, slideCount || 1, metrics),
            });
            setLayoutMeasured(true);
        };

        updateLayout();
        const ro = new ResizeObserver(updateLayout);
        ro.observe(el);
        window.addEventListener('resize', updateLayout);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateLayout);
        };
    }, [isMobileLayout, slideCount]);

    useEffect(() => {
        if (!slideCount) return undefined;

        const progressTimer = setInterval(() => {
            setProgress((prev) => {
                const next = prev + (INTERVAL_TIME / DURATION) * 100;
                return next >= 100 ? 100 : next;
            });
        }, INTERVAL_TIME);

        const slideTimer = setTimeout(() => {
            setActiveSlide(getNextSlideIdLoop());
            setProgress(0);
        }, DURATION);

        return () => {
            clearInterval(progressTimer);
            clearTimeout(slideTimer);
        };
    }, [activeSlide, getNextSlideIdLoop, slideCount]);

    if (!ready) {
        return (
            <section
                className={`hero-section hero-section--loading${isMobileLayout ? ' hero-section--mobile' : ''}`}
                aria-hidden="true"
            />
        );
    }

    if (!slideCount) return null;

    const activeIndex = slides.findIndex((s) => s.id === activeSlide);
    const { activeHeight, metrics: mobileMetrics } = mobileLayout;
    const stripHeight = mobileMetrics.strip;

    return (
        <section
            ref={heroRef}
            className={`hero-section ${isMobileLayout ? 'hero-section--mobile' : 'hero-section--desktop'}`}
        >
            <div className="accordion">
                {slides.map((slide, index) => {
                    const isActive = activeSlide === slide.id;
                    const imageSrc = resolveImageSrc(slide.imageDesktop);
                    const imageBlur = Number(slide.imageBlur ?? 2);
                    const mobileHeight =
                        isMobileLayout && layoutMeasured && activeHeight > 0
                            ? getMobileItemHeight(
                                  index,
                                  activeIndex >= 0 ? activeIndex : 0,
                                  slideCount,
                                  activeHeight,
                                  stripHeight
                              )
                            : null;

                    return (
                        <div
                            key={slide.id}
                            className={`accordion-item ${isActive ? 'active' : ''}`}
                            style={{
                                '--hero-image-blur': `${imageBlur}px`,
                                ...(mobileHeight
                                    ? {
                                          height: `${mobileHeight}px`,
                                          minHeight: `${mobileHeight}px`,
                                      }
                                    : {}),
                            }}
                            onClick={() => handleSlideChange(slide.id)}
                        >
                            <div className="collapsed-info">
                                <div className="css-plus-icon">+</div>
                                <span className="collapsed-text">{slide.collapsedText}</span>
                            </div>

                            <div
                                className={`slide-background ${!isMobileLayout && isActive ? 'desktop-animate animate-in' : ''}`}
                            >
                                <img
                                    src={imageSrc}
                                    alt={slide.imageAlt || slide.collapsedText || 'Hero banner image'}
                                    width="1920"
                                    height="1080"
                                    sizes="100vw"
                                    className="hero-slide-image"
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    fetchPriority={index === 0 ? 'high' : 'auto'}
                                    decoding="async"
                                />
                            </div>

                            <div className="content">
                                <div className="slider-content-spacing-div1">
                                    <p className="slide-headline slider-headline-text">{slide.collapsedText}</p>
                                </div>
                                <div className="slider-content-spacing-div2">
                                    <h1 className="slide-headline">
                                        <MultilineText text={slide.headline} />
                                    </h1>

                                    <div className="slide-progress">
                                        <div
                                            className="slide-progress-bar"
                                            style={{ width: `${isActive ? progress : 0}%` }}
                                        />
                                    </div>

                                    <div className="slide-subcontent">
                                        <span className="slide-subtext">
                                            <MultilineText text={slide.subText} />
                                        </span>
                                        <a href={slide.contactLink} className="d-flex mt-3 slider-contact-btn">
                                            Contact Us <FiArrowRight className="ms-2" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="carousel-dots">
                {slides.map((slide) => (
                    <div
                        key={`dot-${slide.id}`}
                        className={`dot ${activeSlide === slide.id ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSlideChange(slide.id);
                        }}
                    />
                ))}
            </div>
        </section>
    );
};

export default HeroSection;
